import { FiltersData } from "../services/filters.service";
import { FilterStatus } from "./filters";

/**
 * Shared logic for the colorStatus filter: the data transformations behind the
 * colour selection, wherever it is rendered (FilterControlComponent's two
 * chromes, the map and list pages, the store). Only the emit/dispatch timing
 * differs between the callers; everything below is the same for all of them.
 */

// A checkbox row rendered by the colorStatus filter.
export interface ColorStatusRow extends FiltersData {
  key: string;
  keyDisplayed: string;
  checked: boolean;
  activeIcon: string;
  marker: string;
}

// Build the checkbox rows from the store's colorStatus data.
export function buildColorStatusRows(
  data: FilterStatus[],
  showType: string
): ColorStatusRow[] {
  return (data ?? []).map((status) => ({
    key: status.marker,
    keyDisplayed: status.title,
    checked: status[showType],
    activeIcon: status.activeIcon,
    marker: status.marker
  }));
}

// Map a status marker to its color class (matches the $*-state theme vars).
// Orange markers are stored as "orangeStatic", hence the includes() check.
export function markerColorClass(marker: string): string {
  const value: string = (marker ?? "").toLowerCase();
  if (value.includes("orange")) {
    return "orange";
  }
  if (value.includes("green")) {
    return "green";
  }
  if (value.includes("blue")) {
    return "blue";
  }
  if (value.includes("red")) {
    return "red";
  }
  return "grey";
}

// Refresh only the checked state of existing rows from the store data.
export function refreshColorStatusRows(
  rows: ColorStatusRow[],
  data: FilterStatus[],
  showType: string
): ColorStatusRow[] {
  return rows.map((row) => {
    const status = (data ?? []).find((s) => s.marker === row.marker);
    return { ...row, checked: status ? status[showType] : row.checked };
  });
}

// Badge count + "all selected" flag. Both "all selected" and "none selected"
// yield activeData 0 (an empty selection means "no filter", i.e. show all).
export function computeColorStatusActive(
  data: FilterStatus[],
  showType: string
): { allFiltersActive: boolean; activeData: number } {
  const total: number = data?.length ?? 0;
  const filtersActive: number = (data ?? []).filter(
    (filter) => filter[showType]
  ).length;
  return {
    allFiltersActive: filtersActive === total,
    activeData: filtersActive === total ? 0 : filtersActive
  };
}

// Toggle a single row's checked flag (returns a new array).
export function toggleColorStatusRow(
  rows: ColorStatusRow[],
  marker: string
): ColorStatusRow[] {
  return rows.map((row) =>
    row.marker === marker ? { ...row, checked: !row.checked } : row
  );
}

// Uncheck every row (returns a new array). Used by "clear".
export function uncheckColorStatusRows(
  rows: ColorStatusRow[]
): ColorStatusRow[] {
  return rows.map((row) => ({ ...row, checked: false }));
}

// Apply the rows' checked state back onto the store data, producing the
// FilterStatus[] to emit/dispatch.
export function applyColorStatusSelection(
  data: FilterStatus[],
  rows: ColorStatusRow[],
  showType: string
): FilterStatus[] {
  return (data ?? []).map((status) => {
    const row = rows.find((r) => r.marker === status.marker);
    return row ? { ...status, [showType]: row.checked } : status;
  });
}

// ── the selection itself ──────────────────────────────────────────────────────
// The five colour statuses, their labels, and the flag an orange one carries.
// Private to this module: everything outside it goes through the two functions
// below, so the list of markers is written down once.
const STATUS_VALUES = ["green", "blue", "orangeStatic", "red", "grey"];
const STATUS_DESC_VALUES = {
  green: "OK",
  blue: "IN TRANSIT",
  orangeStatic: "STATIC",
  red: "ALERT",
  grey: "QUIET"
};
const FLAGS = {
  orangeStatic: "flagStatic"
};

/**
 * The persisted colour selection, cleaned and migrated, or a fresh default when
 * there is nothing usable to restore. What seeds the store on load.
 */
export function readColorStatusSelection(): FilterStatus[] {
  let statusList;
  try {
    statusList = JSON.parse(localStorage.getItem("statusFilter"));
  } catch {
    // statusFilter holds a corrupt value (e.g. the string "undefined"
    // written when the store was cleared at login): drop it and rebuild.
    localStorage.removeItem("statusFilter");
    statusList = null;
  }
  // Treat an empty array like a missing value. [] is truthy, so without this
  // a stored "[]" would be honoured as a saved selection of zero markers: the
  // colour filters build their rows from this list, so they would render no
  // rows and nothing could ever rebuild the defaults.
  if (!Array.isArray(statusList) || statusList.length === 0) {
    return buildDefaultColorStatusList();
  }

  // Filter out orangeLate and orangeDetour if they exist in localStorage
  statusList = statusList.filter(
    (item) => item.marker !== "orangeLate" && item.marker !== "orangeDetour"
  );
  // A legacy list holding only those retired markers is now empty, which is
  // not a selection either — rebuild instead of persisting "[]".
  if (statusList.length === 0) {
    return buildDefaultColorStatusList();
  }

  // Migrate the old default (everything checked) to the new default
  // (everything unchecked). Both mean "show all", so this is safe and only
  // affects the legacy all-selected state, never a real partial selection.
  const allSelected = statusList.every((item) => item.show && item.showDetail);
  if (allSelected) {
    statusList = statusList.map((item) => ({
      ...item,
      show: false,
      showDetail: false
    }));
  }
  // Update localStorage with cleaned list
  writeColorStatusSelection(statusList);
  return statusList;
}

/**
 * Persist a colour selection — the ONLY writer of `localStorage.statusFilter`,
 * which is why the guard lives here instead of at each of the four store
 * subscriptions that used to inline it.
 *
 * Only a populated list is persisted. `undefined` (the store is cleared at
 * login) would stringify to the string "undefined" and then throw on
 * `JSON.parse`, and an empty array is not a selection — persisting "[]" would
 * leave `readColorStatusSelection` nothing to rebuild from and permanently empty
 * the colour filter.
 */
export function writeColorStatusSelection(list: FilterStatus[]): void {
  if (Array.isArray(list) && list.length) {
    localStorage.setItem("statusFilter", JSON.stringify(list));
  }
}

/**
 * The five colour statuses, all unchecked. Like the other checkbox filters an
 * empty selection means "no filter", so everything is still shown. Not
 * persisted here — the store subscription that receives this list writes it.
 */
function buildDefaultColorStatusList(): FilterStatus[] {
  return STATUS_VALUES.map((status) => {
    const base = {
      marker: status,
      show: false,
      showDetail: false,
      activeIcon: `/assets/img/pin/${status}.svg`,
      title: STATUS_DESC_VALUES[status]
    };
    if (status.includes("orange")) {
      return {
        ...base,
        flag: FLAGS[status],
        activeIcon: `/assets/img/pin/${STATUS_DESC_VALUES[
          status
        ].toLowerCase()}.svg`
      };
    }
    return base;
  });
}
